'use client';

import FeaturedPropertyCardGridCarousel from '@components/CardGridWithMobileCarousel/PropertyGrid/PropertyGrid';
import AdvisorTestimonials from '@components/ClientTestimonials/AdvisorTestimonials';
import ContactForm from '@components/Forms/ContactForm';
import AdvisorBioHeroComponent from '@components/Hero/AdvisorBioHero';
import ThreeColumn from '@components/ThreeColumn';
import { getEntries } from '@data/preview';
import { font, query, space } from '@design/templateFns';
import { ListingSearchResult, StandardStatusEnum } from '@engelvoelkersamericas/api';
import strapiAPI from '@util/api/strapi';
import { useAction } from '@util/customHooks/useAction';
import { extractDomain, extractSubdomain } from '@util/getURL';
import isObjectEmpty from '@util/isObjectEmpty';
import { getCurrentLocale } from '@util/locale';
import { apiClient } from 'api-client';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

interface AdvisorBioPageProps {
  shopIds: string | null;
  advisorData: any;
  isTeam: boolean;
}

const AdvisorBioPage: FC<AdvisorBioPageProps> = observer(({ shopIds, advisorData, isTeam }) => {
  const t = useTranslations();
  const locale = getCurrentLocale();
  const { appStore } = useStores();
  const { urlDomainOverride } = appStore;

  const [activeListingsCustomBtns, setActiveListingsCustomBtns] = useState<
    {
      label: string;
      show: boolean;
      callback: () => void;
    }[]
  >([]);
  const [soldListingsCustomBtns, setSoldListingsCustomBtns] = useState<
    {
      label: string;
      show: boolean;
      callback: () => void;
    }[]
  >([]);
  const [characterLimit, setCharacterLimit] = useState();
  const [ctaUrl, setCtaUrl] = useState<string>('');
  const [activeListingsLimit, setActiveListingsLimit] = useState<number>(6);
  const [soldListingsLimit, setSoldListingsLimit] = useState<number>(6);
  const [contactUsProps, setContactUsProps] = useState<{
    header: string;
    bodyText: string;
    ctaText: string;
    img: string;
    disclaimer?: string;
  } | null>(null);

  const advisorId = isTeam ? advisorData.id : advisorData.listingUserId ?? advisorData.userId;
  const {
    data: activeListingData,
    start: getActiveListings,
    pending: activeListingsLoading,
  } = useAction<ListingSearchResult>({
    action: async () => {
      const tmpShopIds = shopIds?.split(',') ?? undefined;
      const { data } = await apiClient.getListingsByAdvisor({
        id: advisorId,
        size: activeListingsLimit,
        isTeam,
        listingFilters: {
          shopIds: tmpShopIds,
          listingStatuses: [
            StandardStatusEnum.Active,
            StandardStatusEnum.ActiveUnderContract,
            StandardStatusEnum.Pending,
            StandardStatusEnum.ComingSoon,
          ],
        },
      });

      // Temp filter out listings by BuyerAgentFullName until BuyerAgentMlsId is available in data
      const filteredListings = data.listings.filter(
        (listing) => listing.BuyerAgentFullName?.toLowerCase() !== advisorData.name?.toLowerCase(),
      );

      const totalRecords = data.totalRecords - (data.listings.length - filteredListings.length);

      if (activeListingsLimit > 6) {
        setActiveListingsCustomBtns([]);
      } else if (totalRecords > 6) {
        setActiveListingsCustomBtns([
          {
            label: t('View More') + ` (${totalRecords})`,
            show: true,
            callback: () => {
              setActiveListingsLimit(totalRecords);
            },
          },
        ]);
      }

      return { ...data, listings: filteredListings, totalRecords };
    },
    manual: false,
  });

  const {
    data: soldListingData,
    start: getSoldListings,
    pending: soldListingsLoading,
  } = useAction<ListingSearchResult>({
    action: async () => {
      const tmpShopIds = shopIds?.split(',') ?? undefined;
      const { data } = await apiClient.getListingsByAdvisor({
        id: advisorId,
        size: soldListingsLimit,
        isTeam,
        listingFilters: {
          shopIds: tmpShopIds,
          listingStatuses: [StandardStatusEnum.Closed],
        },
      });

      if (soldListingsLimit > 6) {
        setSoldListingsCustomBtns([]);
      } else if (data.totalRecords > 6) {
        setSoldListingsCustomBtns([
          {
            label: t('View More') + ` (${data.totalRecords})`,
            show: true,
            callback: () => {
              setSoldListingsLimit(data.totalRecords);
            },
          },
        ]);
      }

      return data;
    },
    manual: false,
  });

  useEffect(() => {
    getActiveListings();
  }, [activeListingsLimit]);

  useEffect(() => {
    getSoldListings();
  }, [soldListingsLimit]);

  useEffect(() => {
    fetchCharacterLimit();
  }, []);

  const fetchCharacterLimit = async () => {
    const strapiApi = new strapiAPI();
    let characterLimit = await strapiApi.fetchCharacterLimit();
    setCharacterLimit(characterLimit);
  };

  // strip out html tags out of the bio
  if (!isObjectEmpty(advisorData)) {
    advisorData.advisorBio.value = advisorData?.advisorBio?.value.replace(/(<([^>]+)>)/gi, ' ');
  }

  const getDefaultContactUsProps = async () => {
    try {
      const data = await getEntries('6dZASVkryJa7FJoATyoUNa', locale); // default america's contact us form
      if (data) {
        setContactUsProps({
          header: data.fields?.header,
          bodyText: data.fields?.bodyText,
          ctaText: data.fields?.ctaText,
          img: data.fields?.img?.fields?.file?.url,
          disclaimer: data.fields?.disclaimer,
        });
      } else {
        setContactUsProps({
          header: t('ContactForm.title'),
          bodyText: t("Share a few details, and we'll be in touch"),
          img: '/assets/contactformBG.png',
          ctaText: t('Send Message'),
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getDefaultContactUsProps();
  }, [locale]);

  // Form the CTA URL for the listings
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const timer = setTimeout(() => {
      const shopSubdomain = extractSubdomain(advisorData.associatedShops?.url);
      const currentHostSubDomain = extractSubdomain();
      const currentHost = extractDomain(window.location.href, currentHostSubDomain);

      if (shopSubdomain && !isTeam) {
        if (currentHost.includes(shopSubdomain)) {
          setCtaUrl('/properties');
        } else {
          const baseUrl = `${shopSubdomain}.${currentHost}`;
          setCtaUrl(`${window.location.protocol}//${baseUrl}/properties`);
        }
      } else if (isTeam) {
        if (shopSubdomain && window.location.host.includes(shopSubdomain) && urlDomainOverride) {
          setCtaUrl(`${urlDomainOverride}/properties`);
        } else {
          setCtaUrl(`${window.location.protocol}//${window.location.host}/properties`);
        }
      }
    }, 0); // micro-delay to ensure window is truly available after hydration

    return () => clearTimeout(timer);
  }, [advisorData]);

  return (
    <>
      {!isObjectEmpty(advisorData) && (
        <AdvisorBioHeroComponent
          image={
            advisorData.profilePicture
              ? advisorData.profilePicture
              : advisorData.teamPicture || '/assets/textimageplaceholder.png'
          }
          name={advisorData.name}
          title={advisorData.title}
          supplementalTitle={advisorData.evTitle}
          pronouns={advisorData.pronouns}
          specializations={advisorData.specializations}
          designations={advisorData.designations}
          license={advisorData.stateLicenses}
          languages={advisorData.languages}
          phone={advisorData.contact?.phone}
          email={advisorData.contact?.email}
          website={advisorData.contact?.website}
          url={advisorData.contact?.website}
          office={advisorData.associatedShops?.address}
          address2={advisorData.associatedShops?.address2}
          city={advisorData.associatedShops?.city}
          state={advisorData.associatedShops?.state}
          postalCode={advisorData.associatedShops?.postalCode}
          associateShopName={advisorData.associatedShops?.name}
          associateShopHomePage={advisorData.associatedShops?.url}
          socialProfiles={advisorData.socialProfiles}
          isTeamBio={isTeam}
        />
      )}
      {((advisorData.advisorRatings && advisorData.advisorRatings.length > 0) ||
        advisorData.advisorBio?.value) &&
        characterLimit && (
          <AdvisorTestimonials
            testimonials={advisorData.advisorRatings}
            advisorBio={advisorData.advisorBio?.value}
            photo={advisorData.personalMediaPhoto}
            video={advisorData.personalMediaVideo}
            isTeamBio={isTeam}
          />
        )}

      {isTeam && advisorData.associatedAdvisors?.length > 0 && (
        <ThreeColumn header={t('Our Team')} people={advisorData.associatedAdvisors} />
      )}

      {activeListingData && activeListingData.totalRecords > 0 && (
        <FeaturedPropertyCardGridCarousel
          dataTestId="advisor-active-listings"
          searchLink={false}
          header={t(isTeam ? 'Our Listings' : 'My Listings')}
          listings={activeListingData?.listings ?? []}
          ctaUrl={ctaUrl}
          ctaUrlTarget={!!advisorData.associatedShops.url}
          customBtns={activeListingsCustomBtns}
          loading={activeListingsLoading}
          emptyMessage={t(
            isTeam ? 'This team has no active listings' : 'This advisor has no active listings',
          )}
        />
      )}

      {soldListingData && soldListingData.totalRecords > 0 && (
        <FeaturedPropertyCardGridCarousel
          dataTestId="advisor-sold-listings"
          searchLink={false}
          header={t('Sold Properties')}
          listings={soldListingData?.listings ?? []}
          ctaUrl={ctaUrl}
          ctaUrlTarget={!!advisorData.associatedShops.url}
          swatch="cream"
          customBtns={soldListingsCustomBtns}
          loading={soldListingsLoading}
          emptyMessage={t(
            isTeam ? 'This team has no sold listings' : 'This advisor has no sold listings',
          )}
        />
      )}

      {contactUsProps && (
        <ContactForm
          {...contactUsProps}
          chimeId={advisorData.chimeInfoTeamMemberId}
          chimeType={isTeam ? 'Shop' : 'Agent'}
          ownershipId={advisorData.chimeInfoTeamMemberId}
          ownershipScope={'PERSONAL'}
          welcomeEmail={true}
          className="advisor-bio-contact-form"
        />
      )}
    </>
  );
});

export default AdvisorBioPage;

export const NullContainer = styled.div`
  text-align: center;
  margin: auto;
  padding: 20px;

  img {
  }

  @media screen and (${query.atLeast('tablet')}) {
    max-width: 550px;

    img {
      max-width: 550px;
    }
  }
`;

export const SVGContainer = styled.div`
  margin: ${space('x6')} 0;
`;

export const Headline = styled.div`
  ${font('headline-xs')};
  margin-bottom: ${space('lg')};
`;

export const Description = styled.div`
  ${font('body-md-light')};
`;

export const ButtonContainer = styled.div`
  margin: ${space('x8')} 0;

  a {
    margin: ${space('md')} 0;
  }

  @media screen and (${query.atLeast('tablet')}) {
    margin: ${space('x8')};
    a {
      margin: 0 ${space('md')};
    }
  }
`;

export const ImageBox = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 55%;
  position: relative;

  img {
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media screen and (${query.atLeast('tablet')}) {
    padding-bottom: 40%;
  }
`;
